import {
  ProblemSchema,
  formatCustomerMessage,
  isReasonCode,
  toProblem,
  type Problem,
  type ReasonCode,
} from '@paguz/contracts';

/**
 * Error de la API Paguz con su `Problem` (RFC 9457). El mensaje para el cliente es
 * `problem.message` (es-CO, viene de la API) o, si falta, el del catálogo de `reasonCode`.
 * Nunca se muestran códigos crudos de los cores.
 */
export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem, options?: { cause?: unknown }) {
    super(problem.message, options);
    this.name = 'ApiError';
    this.problem = problem;
  }

  get reasonCode(): ReasonCode {
    return this.problem.reasonCode;
  }

  get status(): number {
    return this.problem.status;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

const STATUS_FALLBACK: Readonly<Record<number, ReasonCode>> = {
  400: 'PZ-API-400',
  401: 'PZ-API-401',
  403: 'PZ-API-403',
  404: 'PZ-API-404',
  409: 'PZ-API-409',
  413: 'PZ-API-413',
  415: 'PZ-API-415',
  425: 'PZ-API-425',
  428: 'PZ-API-428',
  429: 'PZ-API-429',
};

/** Construye el `Problem` de una respuesta fallida (tolera cuerpos que no son `Problem`). */
export function problemFromResponse(status: number, body: unknown): Problem {
  const parsed = ProblemSchema.safeParse(body);
  if (parsed.success) {
    const problem = parsed.data;
    return problem.message.trim() === ''
      ? { ...problem, message: formatCustomerMessage(problem.reasonCode) }
      : problem;
  }
  // Cuerpo con reasonCode conocido pero incompleto.
  if (typeof body === 'object' && body !== null && 'reasonCode' in body) {
    const code = body.reasonCode;
    if (typeof code === 'string' && isReasonCode(code)) return toProblem(code, { status });
  }
  return toProblem(STATUS_FALLBACK[status] ?? 'PZ-SYS-001', { status });
}

/** Sin conexión o la API no respondió. */
export function networkError(cause: unknown): ApiError {
  return new ApiError(
    {
      ...toProblem('PZ-SYS-001'),
      message: 'No pudimos conectarnos. Revisa tu internet e inténtalo de nuevo.',
    },
    { cause },
  );
}

/** Mensaje claro para el cliente a partir de cualquier error. */
export function customerMessage(error: unknown): string {
  if (isApiError(error)) return error.problem.message;
  return formatCustomerMessage('PZ-SYS-001');
}
